// SETTINGS-WIRE-1 — SETTINGS RENDERS WHAT ITS FILTER SAYS.
//
// ─── THE OBSTACLE, AND IT WAS OVERSTATED ───────────────────────────────────
//
// MENU-WIRE-1 said this "needs a second activity shape and belongs to the other
// lane". Established properly, that was wrong in the direction that matters.
// `AccountActivity` wants five things and the menu read already answers most:
//
//   canvassPins  -> canvass_pins    ALREADY READ by the menu measure
//   paychecks    -> paychecks       ALREADY READ
//   payPeriods   -> pay_periods     one more count, same sweep
//   serviceAreas -> service_areas   one more count, same sweep
//   modules      -> the section ids the account has, which the nav hop ALREADY
//                   computes; the catalogue's `module` values are section ids
//                   (customers, messages, sales, schedule, settings)
//
// So it is **two more counts and a list already in hand**, not a second shape.
// One read serves both, which is why this wires into the same hop rather than
// opening a second one.
//
// ─── AND THE THIRD TIME THIS WEEK ──────────────────────────────────────────
//
// Build history's exports were rendered by nothing. The menu measure was wired
// by nothing. This filter was imported by nothing. Three things built, measured
// on real accounts, proven, and never connected. The common thread is that a
// wave that ends at "the decision is correct and tested" reads as finished, and
// the guard that would have caught it - does any component call this - is the
// one nobody writes.

import { SETTINGS_CATALOGUE } from './settingsCatalogue'
import { showsInSettings, type AccountActivity, type SettingsEntry } from './settingsVisibility'
import type { MenuActivity } from './menuMeasure'
import type { NavSection } from './navConfig'

/** The two counts the settings filter needs that the menu measure does not. */
export const EXTRA_SETTINGS_TABLES: readonly string[] = ['pay_periods', 'service_areas']

/**
 * ONE READ, TWO FILTERS. Builds the settings filter's shape out of the counts
 * the menu already gathered plus the section ids the nav hop already resolved.
 */
export function settingsActivityFrom(
  counts: MenuActivity,
  sectionIds: readonly string[],
): AccountActivity {
  const n = (t: string) => counts[t] ?? 0
  return {
    modules: sectionIds,
    canvassPins: n('canvass_pins'),
    payPeriods: n('pay_periods'),
    paychecks: n('paychecks'),
    serviceAreas: n('service_areas'),
  }
}

/**
 * THE PAGES THAT ARE THE ONLY WAY IN, kept visible however little the account
 * has done. The same trap MENU-WIRE-1 found in four sections, checked here
 * before wiring rather than after.
 *
 * Each was verified by asking which component writes the table its gate counts:
 *
 *   settings_service_area        `service_areas` is written by ServiceArea.tsx
 *                                and NOTHING ELSE. The page is the only way to
 *                                create the rows that unhide the page.
 *   settings_pay_periods         `pay_periods` is written by lib/payroll.ts from
 *                                two places, and the other one is Team > Payroll
 *                                - which the menu measure hides at zero on
 *                                `paychecks`. Both doors gated on the same
 *                                activity means payroll can never be started.
 *   settings_canvass_dispositions `canvass_dispositions` is written only by
 *                                CanvassDispositionsPage.tsx, and the map that
 *                                creates the pins its gate counts is hidden at
 *                                zero too.
 *
 * `settings_bonuses` is deliberately NOT here, and the distinction is the point:
 * it is not a way in. Bonuses configure something that only exists once payroll
 * is running, and payroll is reachable again through the kept pay periods page,
 * so it unhides on its own. Keeping everything "just in case" would be the same
 * as not filtering.
 */
export const ALWAYS_VISIBLE_SETTINGS: Readonly<Record<string, string>> = {
  settings_service_area:
    'The only component that writes service_areas is this page, so hiding it until an account has one means no account can ever have one.',
  settings_pay_periods:
    'Both doors to payroll are gated on payroll having happened: this page, and Team > Payroll which the menu hides at zero. Kept so payroll can be started at all.',
  settings_canvass_dispositions:
    'The only component that writes canvass_dispositions is this page, and the map that creates the pins its gate counts is hidden at zero as well.',
}

/** Does this settings entry belong on this account's list? */
export function showsInSettingsWired(entry: SettingsEntry, activity: AccountActivity): boolean {
  if (entry.id in ALWAYS_VISIBLE_SETTINGS) return true
  return showsInSettings(entry, activity)
}

/** The settings ids this account should NOT see. */
export function hiddenSettingsIds(activity: AccountActivity): Set<string> {
  const out = new Set<string>()
  for (const entry of SETTINGS_CATALOGUE) {
    if (!showsInSettingsWired(entry, activity)) out.add(entry.id)
  }
  return out
}

/**
 * THE SETTINGS SECTION, FILTERED, AS A FOURTH SUBTRACTIVE LAYER.
 *
 * Only the settings section is touched and only by removal, so it composes with
 * the shelf, the role and the measure in any order exactly as they compose with
 * each other. Settings itself is permanent and is never dropped even if every
 * child went, because it is where a hidden thing is turned back on.
 */
export function applySettingsLayer(
  sections: readonly NavSection[],
  activity: AccountActivity,
): NavSection[] {
  const hidden = hiddenSettingsIds(activity)
  return sections.map((s) => {
    if (s.id !== 'settings') return s
    return { ...s, children: s.children.filter((c) => !hidden.has(c.id)) }
  })
}

/**
 * Every settings entry whose gate counts rows that only that entry's own page
 * can create, and which is not kept visible. The guard requires it to be empty.
 *
 * WRITERS is stated as data because it is a fact about the codebase that was
 * READ, not assumed: each value is the set of components that write the table.
 */
export function trappedSettingsIds(
  writers: Readonly<Record<string, readonly string[]>>,
  keep: Readonly<Record<string, string>> = ALWAYS_VISIBLE_SETTINGS,
): string[] {
  const out: string[] = []
  for (const [id, pages] of Object.entries(writers)) {
    if (id in keep) continue
    // Only that entry's own page can create the rows that unhide it.
    if (pages.length === 1 && pages[0] === id) out.push(id)
  }
  return out
}
